import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useReducer,
} from "react";
import { useParams, Link } from "react-router-dom";
import { httpsCallable } from "firebase/functions";
import { auth, db, functions } from "../firebase";
import {
  RecaptchaVerifier,
  signInWithPhoneNumber,
  isSignInWithEmailLink,
  signInWithEmailLink,
  onAuthStateChanged,
} from "firebase/auth";
import { serverTimestamp, doc, getDoc, setDoc } from "firebase/firestore";
import { useItem } from "../hooks/useItem";
import { useBids } from "../hooks/useBids";
import { isAdminUser } from "../lib/admin";
import Timer from "./Timer";

const BID_INCREMENT = 10;
const ALLOWED_VIEWS = [
  "login",
  "verify",
  "emailSent",
  "emailVerify",
  "name",
  "auction",
];

// Reducer function for managing component state
const initialState = {
  currentView: "login", // 'login', 'verify', 'emailSent', 'emailVerify', 'name', 'auction'
  isLoading: false,
  error: "",
  phoneNumber: "",
  emailAddress: "",
  confirmationResult: null,
  userName: "",
  verificationCode: "",
};

function auctionReducer(state, action) {
  switch (action.type) {
    case "SET_VIEW":
      return { ...state, currentView: action.payload, error: "" }; // Clear error on view change
    case "SET_LOADING":
      return { ...state, isLoading: action.payload };
    case "SET_ERROR":
      return { ...state, error: action.payload, isLoading: false };
    case "SET_PHONE_NUMBER":
      return { ...state, phoneNumber: action.payload };
    case "SET_EMAIL_ADDRESS":
      return { ...state, emailAddress: action.payload };
    case "SET_CONFIRMATION_RESULT":
      return {
        ...state,
        confirmationResult: action.payload,
        currentView: "verify",
        isLoading: false,
        error: "",
      };
    case "SET_VERIFICATION_CODE":
      return { ...state, verificationCode: action.payload };
    case "SET_USER_NAME":
      return { ...state, userName: action.payload };
    case "LOGIN_SUCCESS":
      // Decide next view based on whether name exists (will be checked in useEffect)
      return { ...state, isLoading: false, error: "", verificationCode: "" };
    case "NAME_SAVE_SUCCESS":
      return { ...state, currentView: "auction", isLoading: false, error: "" };
    case "AUTH_STATE_KNOWN":
      return {
        ...state,
        currentView: action.payload.view,
        userName: action.payload.name || "",
      };
    case "LOGOUT":
      return { ...initialState, currentView: "login" }; // Reset most state on logout
    default:
      return state;
  }
}

// --- Child Components for Different Views ---

function LoginView({
  phoneNumber,
  dispatch,
  handleSendCode,
  isLoading,
  error,
  item,
  bids,
}) {
  return (
    <div className="p-6 bg-white rounded-lg shadow-md max-w-md mx-auto">
      <h2 className="text-2xl font-bold text-orange-600 mb-4 text-center">
        Connectez-vous pour enchérir
      </h2>
      <div className="mb-4">
        <label className="block text-gray-700 mb-2" htmlFor="phone">
          Entrez votre numéro de mobile pour recevoir un code de connexion:
        </label>
        <input
          type="tel"
          id="phone"
          className="w-full p-3 border border-gray-300 rounded-md"
          placeholder="ex: 0490000000"
          value={phoneNumber}
          onChange={(e) =>
            dispatch({ type: "SET_PHONE_NUMBER", payload: e.target.value })
          }
          disabled={isLoading}
        />
        {error && <p className="text-red-500 mt-2 text-sm">{error}</p>}
      </div>
      <div id="recaptcha-container" className="hidden" />
      <button
        type="button"
        onClick={handleSendCode}
        className="w-full bg-orange-500 hover:bg-orange-600 text-white font-bold py-3 px-4 rounded-md disabled:opacity-50"
        disabled={isLoading}
      >
        {isLoading ? "Envoi en cours..." : "Envoyer le Code Connexion"}
      </button>
      <p className="mt-4 text-center text-gray-600 text-sm">
        Nous vous enverrons un code accéder aux enchères.
      </p>
      <div className="bg-orange-50 p-4 rounded-lg mt-6 mb-6">
        <div className="flex justify-center mb-3">
          {item.image ? (
            <img
              src={item.image}
              alt={item.name}
              className="rounded-md w-full max-w-xs h-auto"
            />
          ) : (
            <div className="rounded-md w-full max-w-xs min-h-48 bg-orange-100 text-orange-700 font-semibold flex items-center justify-center text-center px-6">
              Image à venir
            </div>
          )}
        </div>
        <h3 className="font-bold text-lg">{item.name}</h3>
        <p className="text-gray-700 mb-2">{item.description}</p>
        {bids.length > 0 && (
          <div className="mt-4">
            <h4 className="font-bold mb-2">Dernières enchères:</h4>
            <ul className="text-sm">
              {bids.slice(0, 5).map((bid) => (
                <li
                  key={bid.id}
                  className="flex justify-between py-1 border-b border-orange-100"
                >
                  <span>{bid.amount}€</span>
                  <span className="text-gray-600">
                    {bid.placedAt?.toDate().toLocaleTimeString()}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

function VerifyView({
  verificationCode,
  dispatch,
  handleVerify,
  isLoading,
  error,
}) {
  return (
    <div className="max-w-md mx-auto bg-white p-6 rounded shadow">
      <h2 className="text-xl font-bold mb-4">Vérification du code</h2>
      <input
        type="text"
        placeholder="123456"
        value={verificationCode}
        onChange={(e) =>
          dispatch({ type: "SET_VERIFICATION_CODE", payload: e.target.value })
        }
        className="w-full p-2 border mb-4"
        disabled={isLoading}
      />
      {error && <p className="text-red-500 mb-2">{error}</p>}
      <button
        type="button"
        onClick={handleVerify}
        className="w-full bg-orange-500 text-white py-2 rounded disabled:opacity-50"
        disabled={isLoading}
      >
        {isLoading ? "Vérification..." : "Vérifier"}
      </button>
    </div>
  );
}

function EmailVerifyView({
  emailAddress,
  dispatch,
  handleVerifyEmailLink,
  error,
}) {
  return (
    <div className="max-w-md mx-auto bg-white p-6 rounded shadow">
      <h2 className="text-xl font-bold mb-4">Vérification du lien email</h2>
      <input
        type="email"
        placeholder="vous@exemple.com"
        value={emailAddress}
        onChange={(e) =>
          dispatch({ type: "SET_EMAIL_ADDRESS", payload: e.target.value })
        }
        className="w-full p-2 border mb-4"
      />
      {error && <p className="text-red-500 mb-2">{error}</p>}
      <button
        type="button"
        onClick={handleVerifyEmailLink}
        className="w-full bg-orange-500 text-white py-2 rounded"
      >
        Confirmer l'email
      </button>
    </div>
  );
}

function NameView({ userName, dispatch, handleSaveName, isLoading, error }) {
  return (
    <div className="max-w-md mx-auto bg-white p-6 rounded shadow">
      <h2 className="text-xl font-bold mb-4">Votre nom</h2>
      <p className="mb-4 text-gray-600">
        Veuillez entrer votre nom pour participer aux enchères.
      </p>
      <input
        type="text"
        placeholder="Votre nom"
        value={userName}
        onChange={(e) =>
          dispatch({ type: "SET_USER_NAME", payload: e.target.value })
        }
        className="w-full p-2 border mb-4"
        disabled={isLoading}
      />
      {error && <p className="text-red-500 mb-2">{error}</p>}
      <button
        type="button"
        onClick={handleSaveName}
        className="w-full bg-orange-500 text-white py-2 rounded disabled:opacity-50"
        disabled={isLoading}
      >
        {isLoading ? "Enregistrement..." : "Continuer"}
      </button>
    </div>
  );
}

function AuctionDisplayView({
  item,
  bids,
  bidAmount,
  handleBidAmountChange,
  setBidAmount,
  formatBidAmount,
  paymentCommitment,
  setPaymentCommitment,
  handleBid,
  isLoading,
  error,
  getHighestBid,
}) {
  const highest = getHighestBid();
  const current = highest ? highest.amount : item.startingPrice;
  const minBid = current + BID_INCREMENT;
  const user = auth.currentUser;
  // Add state to track if user is actively editing to avoid resetting while typing
  const [isEditing, setIsEditing] = useState(false);
  // Add state to track if the auction has ended
  const [isEnded, setIsEnded] = useState(false);
  const [winnerName, setWinnerName] = useState("");

  // Function to fetch the winner's name from their userId using useCallback
  const fetchWinnerName = useCallback(async (userId) => {
    if (!userId) return;

    try {
      const userDoc = await getDoc(doc(db, "users", userId));
      if (userDoc.exists()) {
        setWinnerName(userDoc.data().name || "Enchérisseur anonyme");
      } else {
        setWinnerName("Enchérisseur anonyme");
      }
    } catch (err) {
      console.error("Error fetching winner's name:", err);
      setWinnerName("Enchérisseur anonyme");
    }
  }, []);

  // Check if auction has ended when component mounts
  useEffect(() => {
    if (item && item.endDate) {
      const now = new Date();
      const endDate = item.endDate.toDate();
      const auctionEnded = now > endDate;
      setIsEnded(auctionEnded);

      // If auction has ended and there's a highest bid, fetch the winner's name
      if (auctionEnded && highest) {
        fetchWinnerName(highest.userId);
      }
    }
  }, [item, highest, fetchWinnerName]);

  // Add handler for blur validation
  const handleBidBlur = () => {
    setIsEditing(false);

    // Calculate minimum bid based on current state
    let minBid;
    if (bids.length > 0) {
      const highest = getHighestBid();
      minBid = highest
        ? highest.amount + BID_INCREMENT
        : item.startingPrice + BID_INCREMENT;
    } else {
      minBid = item.startingPrice + BID_INCREMENT;
    }

    // When field loses focus, if empty or below minimum, set to minimum bid
    if (bidAmount === "" || bidAmount < minBid) {
      console.log("Resetting bid to minimum on blur:", minBid);
      setBidAmount(minBid);
    }
  };

  // Handle focus to prevent auto-updates while user is typing
  const handleBidFocus = () => {
    setIsEditing(true);
  };

  // Initialize bid amount when component mounts and update when bids change
  useEffect(() => {
    // Only proceed if we have the item data and user is not actively editing
    if (item && !isEditing) {
      let minBid;

      if (bids.length > 0) {
        const highest = getHighestBid();
        minBid = highest
          ? highest.amount + BID_INCREMENT
          : item.startingPrice + BID_INCREMENT;
      } else {
        minBid = item.startingPrice + BID_INCREMENT;
      }

      // Set bid amount in these cases:
      // 1. When field is empty (but not when user is actively editing)
      // 2. When current bid amount is less than the new minimum (e.g., when a new bid came in)
      if ((bidAmount === "" && !isEditing) || bidAmount < minBid) {
        console.log(
          "Updating bid amount to new minimum:",
          minBid,
          "current:",
          bidAmount
        );
        setBidAmount(minBid);
      }
    }
    // bidAmount intentionally included in deps array to detect when it's below minimum
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getHighestBid, item, bids, bidAmount, isEditing]); // Added isEditing to deps

  return (
    <div className="max-w-3xl mx-auto bg-white p-6 rounded shadow">
      <div className="bg-orange-50 p-4 rounded-lg mb-6">
        <div className="flex justify-center mb-3">
          {item.image ? (
            <img
              src={item.image}
              alt={item.name}
              className="rounded-md w-full max-w-xs h-auto"
            />
          ) : (
            <div className="rounded-md w-full max-w-xs min-h-48 bg-orange-100 text-orange-700 font-semibold flex items-center justify-center text-center px-6">
              Image à venir
            </div>
          )}
        </div>
        <h3 className="font-bold text-lg">{item.name}</h3>
        <p className="text-gray-700 mb-2">{item.description}</p>
        <div className="mt-4">
          <h4 className="font-bold mb-2">Temps restant:</h4>
          <Timer
            endDate={item.endDate.toDate()}
            onEnd={() => {
              setIsEnded(true);
              if (highest) {
                fetchWinnerName(highest.userId);
              }
            }}
          />
          {isEnded && highest && (
            <div className="mt-4 bg-green-100 p-4 rounded-lg">
              <h4 className="font-bold text-green-800">Enchère terminée!</h4>
              <p className="text-green-700">
                L'enchérisseur gagnant est:{" "}
                <span className="font-bold">{winnerName}</span>
              </p>
              <p className="text-green-700">
                Prix final: <span className="font-bold">{highest.amount}€</span>
              </p>
            </div>
          )}
          {isEnded && !highest && (
            <div className="mt-4 bg-yellow-100 p-4 rounded-lg">
              <p className="text-yellow-700">
                Aucune enchère n'a été placée pour cet article.
              </p>
            </div>
          )}
        </div>
      </div>
      {!isEnded && (
        <>
          <div className="mb-4">
            <p className="font-bold">Enchère actuelle: {current}€</p>
            <p className="text-sm text-gray-600">(Min +10€)</p>
          </div>
          <div className="mb-4">
            <label className="block text-gray-700 mb-2">
              Montant de votre enchère:
            </label>
            <div className="relative">
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={formatBidAmount(bidAmount)}
                onChange={handleBidAmountChange}
                className="w-full p-3 border rounded text-lg"
                disabled={isLoading || isEnded}
                placeholder={`${minBid}€`}
                onBlur={handleBidBlur}
                onFocus={handleBidFocus}
              />
              {bidAmount !== "" && (
                <button
                  type="button"
                  onClick={() => setBidAmount("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  ×
                </button>
              )}
            </div>
            <p className="text-sm text-gray-500 mt-1">
              Enchère minimum: {minBid}€
            </p>
          </div>
          <div className="mb-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={paymentCommitment}
                onChange={(e) => setPaymentCommitment(e.target.checked)}
                className="mr-2"
                disabled={isLoading || isEnded}
              />
              <span>
                Je m'engage à payer la somme de mon enchère si je l'ai gagnée (
                <Link
                  to="/terms"
                  state={{ fromItem: true }}
                  className="text-blue-500 hover:text-blue-700"
                >
                  voir les conditions générales
                </Link>
                )
              </span>
            </label>
          </div>
          {error && <p className="text-red-500 mb-2">{error}</p>}
          <button
            type="button"
            onClick={handleBid}
            className="w-full bg-orange-500 text-white py-2 rounded mb-6 disabled:opacity-50"
            disabled={isLoading || !paymentCommitment || !bidAmount || isEnded}
          >
            {isLoading
              ? "Envoi en cours..."
              : isEnded
              ? "Enchère terminée"
              : "Placer l'enchère"}
          </button>
        </>
      )}
      <h3 className="text-xl font-bold mb-2">
        Historique des 10 dernières enchères
      </h3>
      {bids.length === 0 ? (
        <p className="text-gray-600">Aucune enchère pour l'instant.</p>
      ) : (
        <ul>
          {bids.slice(0, 10).map((b) => (
            <li key={b.id} className="flex justify-between py-2 border-b">
              <span>{b.amount}€</span>
              <span className="text-sm text-gray-600">
                {user &&
                (b.bidder === user.phoneNumber || b.bidder === user.email)
                  ? "Votre enchère"
                  : ""}
              </span>
              <span className="text-sm text-gray-600">
                {b.placedAt?.toDate().toLocaleTimeString()}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// --- Main Auction Page Component ---

export default function AuctionPage() {
  const { slug } = useParams();
  const { item, loading: itemLoading, error: itemError } = useItem(slug);
  const { bids, error: bidsError } = useBids(item?.id);
  const [bidAmount, setBidAmount] = useState("");
  const [paymentCommitment, setPaymentCommitment] = useState(false);
  const recaptchaRef = useRef(null);
  const [isAdmin, setIsAdmin] = useState(false);

  const [state, dispatch] = useReducer(auctionReducer, initialState);
  const {
    currentView,
    isLoading,
    error,
    phoneNumber,
    emailAddress,
    confirmationResult,
    userName,
    verificationCode,
  } = state;

  // Detect email link sign-in
  useEffect(() => {
    if (isSignInWithEmailLink(auth, window.location.href)) {
      dispatch({ type: "SET_VIEW", payload: "emailVerify" });
    }
  }, []);

  // Check authentication state and user name on mount
  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
      if (user) {
        console.log("User is signed in:", user.phoneNumber || user.email);
        setIsAdmin(isAdminUser(user));

        try {
          const userDoc = await getDoc(doc(db, "users", user.uid));
          const userData = userDoc.exists() ? userDoc.data() : {};
          console.log("User document data:", userData);

          if (userData?.name) {
            console.log("User has a name, going to auction view");
            dispatch({
              type: "AUTH_STATE_KNOWN",
              payload: { view: "auction", name: userData.name },
            });
          } else {
            console.log("User needs to set a name, going to name view");
            dispatch({
              type: "AUTH_STATE_KNOWN",
              payload: { view: "name", name: "" },
            });
          }
        } catch (err) {
          console.error("Error checking user name:", err);
          let checkError = "Erreur lors de la vérification du nom";
          if (err.code === "permission-denied") {
            console.log(
              "Permission denied, assuming new user needs to set name"
            );
            checkError =
              "Erreur de permission lors de la vérification du nom. Êtes-vous connecté?"; // More specific error
          }
          // Set view to 'name' even if check fails, but display error
          dispatch({
            type: "AUTH_STATE_KNOWN",
            payload: { view: "name", name: "" },
          });
          dispatch({ type: "SET_ERROR", payload: checkError });
        }

        // Clear reCAPTCHA if it exists (might still be needed if login flow was interrupted)
        if (recaptchaRef.current) {
          try {
            recaptchaRef.current.clear();
          } catch (error) {
            console.warn("Error clearing reCAPTCHA after auth success:", error);
          }
        }
      } else {
        console.log("No user is signed in");
        dispatch({ type: "LOGOUT" });
        setIsAdmin(false);
        setTimeout(setupRecaptcha, 1000); // Setup reCAPTCHA for login view
      }
    });

    return () => {
      console.log("Unsubscribing from auth listener");
      unsubscribeAuth();
      // Cleanup reCAPTCHA instance if component unmounts
      if (recaptchaRef.current) {
        try {
          // Attempt to find the widget instance and reset it
          const widgetId = recaptchaRef.current.widgetId;
          if (
            typeof window !== "undefined" &&
            window.grecaptcha &&
            widgetId !== undefined
          ) {
            window.grecaptcha.reset(widgetId);
            console.log("reCAPTCHA reset successfully on unmount.");
          } else {
            recaptchaRef.current.clear(); // Fallback
          }
        } catch (error) {
          console.error("Error resetting reCAPTCHA on unmount:", error);
        }
      }
    };
  }, []);

  // Initialize reCAPTCHA
  const setupRecaptcha = async () => {
    try {
      // Wait for the container to be mounted and visible
      const container = document.getElementById("recaptcha-container");
      if (!container) {
        throw new Error("reCAPTCHA container not found");
      }

      // Clear any existing reCAPTCHA
      if (recaptchaRef.current) {
        try {
          recaptchaRef.current.clear();
        } catch (error) {
          console.warn("Error clearing existing reCAPTCHA:", error);
        }
      }

      // Create a new invisible reCAPTCHA verifier
      recaptchaRef.current = new RecaptchaVerifier(auth, container, {
        size: "invisible",
        callback: (response) => {
          console.log("reCAPTCHA solved:", response);
        },
        "expired-callback": () => {
          console.log("reCAPTCHA expired");
          setupRecaptcha();
        },
      });

      // Verify the reCAPTCHA is properly initialized
      if (!recaptchaRef.current) {
        throw new Error("Failed to initialize reCAPTCHA");
      }

      // Render the reCAPTCHA
      await recaptchaRef.current.render();
      console.log("reCAPTCHA widget rendered successfully");
    } catch (error) {
      console.error("reCAPTCHA setup error:", error);
      dispatch({
        type: "SET_ERROR",
        payload: `Erreur lors de l'initialisation de la vérification: ${error.message}`,
      });
      // Retry initialization after a delay
      setTimeout(setupRecaptcha, 2000);
    }
  };

  const getHighestBid = useCallback(() => {
    if (bids.length === 0) return null;
    return bids[0];
  }, [bids]);

  // Handle bid amount input
  const handleBidAmountChange = (e) => {
    const value = e.target.value;
    // Remove any non-digit characters
    const cleanValue = value.replace(/[^\d]/g, "");
    // If input was cleared, set to empty string to allow starting fresh
    // Otherwise convert to number and set state
    setBidAmount(value === "" ? "" : cleanValue ? Number(cleanValue) : "");
  };

  // Format bid amount for display
  const formatBidAmount = (value) => {
    if (value === "") return "";
    return value.toString();
  };

  // Send SMS code
  const handleSendCode = async () => {
    dispatch({ type: "SET_LOADING", payload: true });
    dispatch({ type: "SET_ERROR", payload: "" });

    try {
      // Format phone number properly
      let cleanNumber = phoneNumber;
      if (phoneNumber.startsWith("0")) {
        cleanNumber = phoneNumber.replace("0", "+32");
      }
      cleanNumber = cleanNumber.replace(/[^\d+]/g, "");
      /* Only add +32 if the number doesn't already start with +32 */
      console.log(cleanNumber);
      const formattedPhoneNumber = cleanNumber.startsWith("+32")
        ? cleanNumber
        : `+32${cleanNumber.replace(/^0/, "")}`;

      console.log(
        "Attempting to send verification code to:",
        formattedPhoneNumber
      );

      if (!recaptchaRef.current) {
        // Try to initialize reCAPTCHA if not already done
        await setupRecaptcha();
        if (!recaptchaRef.current) {
          throw new Error(
            "reCAPTCHA non initialisé. Veuillez rafraîchir la page."
          );
        }
      }

      // Execute reCAPTCHA
      try {
        const recaptchaResponse = await recaptchaRef.current.verify();
        console.log("reCAPTCHA verified:", recaptchaResponse);
      } catch (error) {
        console.warn("reCAPTCHA verification warning:", error);
        // Continue anyway as the verification might still work
      }

      const verification = await signInWithPhoneNumber(
        auth,
        formattedPhoneNumber,
        recaptchaRef.current
      );
      dispatch({ type: "SET_CONFIRMATION_RESULT", payload: verification });
    } catch (err) {
      console.error("Login error:", err);
      const rawCode = err?.code || "";
      const rawMessage = err?.message || "";
      let errorMessage = rawMessage || "Erreur lors de l'envoi du SMS";

      if (rawCode === "auth/invalid-phone-number") {
        errorMessage = "Numéro de téléphone invalide";
      } else if (rawCode === "auth/invalid-app-credential") {
        errorMessage =
          "Erreur de configuration Firebase. Veuillez vérifier les paramètres du projet.";
      } else if (rawCode === "auth/too-many-requests") {
        errorMessage = "Trop de tentatives. Veuillez réessayer plus tard.";
      } else if (
        rawCode === "auth/error-code:-39" ||
        rawMessage.includes("auth/error-code:-39") ||
        rawMessage.includes("503")
      ) {
        errorMessage =
          "Le service de vérification SMS est temporairement indisponible. Veuillez réessayer dans quelques minutes.";
      }
      dispatch({ type: "SET_ERROR", payload: errorMessage });
    }
  };

  // Verify code
  const handleVerify = async () => {
    dispatch({ type: "SET_LOADING", payload: true });
    dispatch({ type: "SET_ERROR", payload: "" });

    try {
      if (!verificationCode || verificationCode.length !== 6) {
        throw new Error("Code invalide");
      }

      await confirmationResult.confirm(verificationCode);
      dispatch({ type: "LOGIN_SUCCESS" });
    } catch (err) {
      console.error("Verification error:", err);
      let errorMessage = "Erreur lors de la vérification du code";
      if (err.code === "auth/invalid-verification-code") {
        errorMessage = "Code de vérification invalide";
      } else if (err.code === "auth/code-expired") {
        errorMessage = "Le code a expiré. Veuillez demander un nouveau code.";
        dispatch({ type: "SET_VIEW", payload: "login" });
      }
      dispatch({ type: "SET_ERROR", payload: errorMessage });
    }
  };

  // Verify email link and sign in
  const handleVerifyEmailLink = async () => {
    dispatch({ type: "SET_LOADING", payload: true });
    dispatch({ type: "SET_ERROR", payload: "" });

    try {
      let email = emailAddress;
      if (!email) {
        email = window.localStorage.getItem("emailForSignIn");
      }
      await signInWithEmailLink(auth, email, window.location.href);
      window.localStorage.removeItem("emailForSignIn");
      dispatch({ type: "LOGIN_SUCCESS" });
    } catch (err) {
      console.error(err);
      dispatch({
        type: "SET_ERROR",
        payload: "Erreur lors de la vérification du lien email",
      });
    }
  };

  // Save user name
  const handleSaveName = async () => {
    dispatch({ type: "SET_LOADING", payload: true });
    dispatch({ type: "SET_ERROR", payload: "" });

    try {
      if (!userName.trim()) {
        throw new Error("Veuillez entrer votre nom");
      }

      const user = auth.currentUser;
      if (!user) {
        throw new Error("Utilisateur non connecté");
      }

      console.log("Saving name for user:", user.uid);

      // Save user name to Firestore
      await setDoc(
        doc(db, "users", user.uid),
        {
          name: userName.trim(),
          phoneNumber: user.phoneNumber,
          createdAt: serverTimestamp(),
        },
        { merge: true }
      ); // Use merge to not overwrite other fields if they exist

      console.log("Name saved successfully");
      dispatch({ type: "NAME_SAVE_SUCCESS" });
    } catch (err) {
      console.error("Error saving name:", err);
      let errorMessage = err.message || "Erreur lors de la sauvegarde du nom";
      if (err.code === "permission-denied") {
        errorMessage = "Erreur de permissions. Veuillez rafraîchir la page.";
      }
      dispatch({ type: "SET_ERROR", payload: errorMessage });
    }
  };

  // Place bid
  const handleBid = async () => {
    if (!item) return;
    dispatch({ type: "SET_LOADING", payload: true });
    dispatch({ type: "SET_ERROR", payload: "" });

    try {
      const highest = getHighestBid();
      const minBid = highest
        ? highest.amount + BID_INCREMENT
        : item.startingPrice + BID_INCREMENT;
      if (bidAmount < minBid) {
        throw new Error(`Enchère minimum: ${minBid}€`);
      }

      if (!paymentCommitment) {
        throw new Error("Veuillez accepter l'engagement de paiement");
      }

      const user = auth.currentUser;
      if (!user) {
        throw new Error("Utilisateur non connecté");
      }

      const placeBid = httpsCallable(functions, "placeBid");
      await placeBid({
        itemId: item.id,
        amount: bidAmount,
      });
      setPaymentCommitment(false);
      setBidAmount("");
    } catch (err) {
      console.error("Bid error:", err);
      dispatch({
        type: "SET_ERROR",
        payload: err.message || "Erreur lors du placement de l'enchère",
      });
    } finally {
      dispatch({ type: "SET_LOADING", payload: false });
    }
  };

  // Handle Loading and Error States from Hooks
  if (itemLoading) {
    return <p className="p-6">Chargement de l'article...</p>;
  }
  if (itemError) {
    return <p className="p-6 text-red-600">Erreur: {itemError.message}</p>;
  }
  if (!item && !itemLoading) {
    // Added check for !item after loading is complete
    return <p className="p-6">Article introuvable.</p>;
  }
  // Optionally display bids loading/error state, or handle within components
  if (bidsError) {
    // Decide how to handle bids error - maybe show a message in the auction view?
    console.error("Bids Error:", bidsError.message);
    // For now, let's log it and proceed, the AuctionDisplayView will show 0 bids
  }

  const safeCurrentView = ALLOWED_VIEWS.includes(currentView)
    ? currentView
    : "login";

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-4">
        <Link to="/" className="text-blue-500 underline">
          &larr; Retour aux articles
        </Link>
        {safeCurrentView === "auction" && (
          <div className="flex items-center gap-4">
            {isAdmin && (
              <Link to="/admin" className="text-blue-500 underline">
                Administration
              </Link>
            )}
            <button
              type="button"
              onClick={() => {
                auth.signOut();
                dispatch({ type: "LOGOUT" });
              }}
              className="text-blue-500 underline"
            >
              Se déconnecter
            </button>
          </div>
        )}
      </div>

      {safeCurrentView === "login" && (
        <LoginView
          phoneNumber={phoneNumber}
          dispatch={dispatch}
          handleSendCode={handleSendCode}
          isLoading={isLoading}
          error={error}
          item={item}
          bids={bids}
        />
      )}
      {safeCurrentView === "verify" && (
        <VerifyView
          verificationCode={verificationCode}
          dispatch={dispatch}
          handleVerify={handleVerify}
          isLoading={isLoading}
          error={error}
        />
      )}
      {safeCurrentView === "emailSent" && (
        <div className="max-w-md mx-auto bg-white p-6 rounded shadow">
          <h2 className="text-xl font-bold mb-4">Vérifiez votre email</h2>
          <p>
            Un lien de connexion a été envoyé à {emailAddress}. Ouvrez votre
            boîte mail et cliquez dessus.
          </p>
        </div>
      )}
      {safeCurrentView === "emailVerify" && (
        <EmailVerifyView
          emailAddress={emailAddress}
          dispatch={dispatch}
          handleVerifyEmailLink={handleVerifyEmailLink}
          error={error}
        />
      )}
      {safeCurrentView === "name" && (
        <NameView
          userName={userName}
          dispatch={dispatch}
          handleSaveName={handleSaveName}
          isLoading={isLoading}
          error={error}
        />
      )}
      {safeCurrentView === "auction" && (
        <AuctionDisplayView
          item={item}
          bids={bids}
          bidAmount={bidAmount}
          handleBidAmountChange={handleBidAmountChange}
          setBidAmount={setBidAmount}
          formatBidAmount={formatBidAmount}
          paymentCommitment={paymentCommitment}
          setPaymentCommitment={setPaymentCommitment}
          handleBid={handleBid}
          isLoading={isLoading}
          error={error}
          getHighestBid={getHighestBid}
        />
      )}
    </div>
  );
}
